import { useQuery } from '@tanstack/react-query';
import { SourcingService } from 'api-clients/monolith';
import { Suspense, useMemo } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { FormattedMessage } from 'react-intl';

import { Typography } from '@/components/ui/typography';
import { useOpeningDetails } from '@/contexts/opening-context';
import { useOpeningSlug } from '@/hooks/use-opening-slug';
import { useShowHiringGoalEmptyState } from '@/components/pages/channels/use-show-hiring-goal-empty-state';
import { queryKeys } from '@/query-keys';
import { reportBoundaryError } from '@/utils/sentry';

import { Card } from './card';

export function GeneralRecommendationContent() {
  const openingSlug = useOpeningSlug();
  const { opening, hiringGoal } = useOpeningDetails();
  const canPostOpening = opening.postingIneligibilityReason === null;
  const showHiringGoalEmptyState = useShowHiringGoalEmptyState(hiringGoal);

  const shouldFetch =
    Boolean(hiringGoal?.external_id) &&
    Boolean(hiringGoal?.is_current) &&
    Boolean(opening?.hasActiveHiringGoal);

  const { data } = useQuery({
    queryKey: [
      ...queryKeys.openingDetails.channels.generalRecommendation,
      openingSlug,
    ],
    queryFn: () =>
      SourcingService.getInternalApiSourcingOpeningsGeneralRecommendations(
        openingSlug,
      ),
    enabled: !showHiringGoalEmptyState && shouldFetch && canPostOpening,
  });

  const recommendedChannels = useMemo(
    () => data?.recommended_channels ?? [],
    [data],
  );

  if (
    showHiringGoalEmptyState ||
    !shouldFetch ||
    !canPostOpening ||
    recommendedChannels.length <= 0
  )
    return null;

  return (
    <>
      <Typography as="h2" variant="caption">
        <FormattedMessage
          defaultMessage="General recommendations"
          description="Section heading for org-wide suggested next steps"
        />
      </Typography>
      <div className="flex gap-4">
        <Card channels={recommendedChannels} />
      </div>
    </>
  );
}

/**
 * Wrapped in its own Suspense boundary so its `useOpeningDetails()` call
 * doesn't block sibling sections (Analytics, ChannelsSection, etc.) in
 * `ChannelsContent` from mounting and firing their own fetches.
 */
export function GeneralRecommendation() {
  return (
    <ErrorBoundary
      onError={reportBoundaryError('channels.general-recommendations')}
      fallback={null}
    >
      <Suspense fallback={null}>
        <GeneralRecommendationContent />
      </Suspense>
    </ErrorBoundary>
  );
}
